import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Every navigation lands at the top of the page, the way a full page load
 * would - without this, clicking a header, footer or menu link keeps whatever
 * scroll offset the previous page had.
 *
 * Two details this has to respect:
 *
 * - `scroll-behavior: smooth` is set globally, so a plain `scrollTo` would
 *   animate the entire page on every route change. Route changes jump; only
 *   in-page anchors animate.
 * - Routes are lazily loaded, so a `#hash` target is usually not in the DOM on
 *   the frame the location changes. We look for it across a few frames before
 *   giving up rather than silently landing in the wrong place.
 */
const HASH_TARGET_TIMEOUT_MS = 1000;

export function ScrollToTop() {
  const { hash, key } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    const giveUpAt = performance.now() + HASH_TARGET_TIMEOUT_MS;
    let frame = 0;

    const scrollToTarget = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView();
      } else if (performance.now() < giveUpAt) {
        frame = requestAnimationFrame(scrollToTarget);
      }
    };

    frame = requestAnimationFrame(scrollToTarget);
    return () => cancelAnimationFrame(frame);
    // `key` changes on every navigation, so re-clicking the same link re-scrolls.
  }, [hash, key]);

  return null;
}
